import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { cardHref } from '@/lists/cellValue'
import { useCallAction } from '@/telephony/CallButton'
import { Composer, NextStepBlock, RecordFeed, useComposerModes, useRecordActivity, type ActivityTarget } from './activityKit'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { fetchDuplicates } from './counterpartyApi'
import { fetchFunnels } from './dealApi'
import { HistoryPanel } from './HistoryPanel'
import { LeadScorePanel } from '@/leadscore/LeadScorePanel'
import { TaskProposalsPanel } from '@/ai/TaskProposals'
import {
  convertLead,
  fetchLead,
  fetchLeadDuplicates,
  mergeLead,
  qualifyLead,
  recordLeadResponse,
  rejectLead,
  type LeadView,
} from './leadApi'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
  type StageStep,
} from './record'
import { listPath } from './recordHelpers'
import {
  FailureAlert,
  FormModal,
  RecordFailure,
  RecordSkeleton,
  RequiredText,
  StatusBadge,
  copyLinkItem,
  failureText,
  labelOf,
} from './recordKit'
import type { RecordViewProps } from './types'

/**
 * Карточка лида на дизайн-системе (Lead.dc.html, тикет #101).
 *
 * Вкладки «Действия» больше нет: действия — в шапке по статусу. Новый и «в работе» лид
 * квалифицируют, квалифицированный — конвертируют (контрагент, контакт и сделка одной
 * транзакцией, и карточка ведёт прямо в сделку). Отказ — в «⋯» за чертой и только с
 * причиной: отклонённый «просто так» лид не отвечает на вопрос, почему канал приносит не тех.
 *
 * Таймер SLA первого отклика (`.sla`) — из сроков сервера (`firstResponseDueAt`,
 * `firstResponseAt`); «просрочено» — по флагу сервера `slaBreached`, тому же, по которому
 * лид попадает в отчёт руководителю. «Ответили клиенту» останавливает часы отдельным
 * действием: ответить можно, ещё не решив, годен ли лид.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - **Текст заявки, форма, страница, `utm_medium`/`utm_term`, объём и бюджет из заявки** —
 *   API лида их не отдаёт (`LeadView`: только `utmSource`, `utmCampaign`).
 * - **Подбор товара по заявке** и **чек-лист квалификации** (потребность, бюджет, срок,
 *   ЛПР) — ни сущности, ни эндпоинта.
 * - **«Передать другому менеджеру»**: `POST /sales/leads/{id}/assign` есть, но выбор
 *   сотрудника требует чтения `hr.employee`, которого у менеджера продаж нет.
 * - **Имена авторов в ленте**: событие несёт `actorId` без имени (как у сделки).
 */
export function LeadRecordView({ recordId, record, context, form, notices, edit }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState('overview')
  const [failure, setFailure] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [converting, setConverting] = useState(false)
  const [funnelId, setFunnelId] = useState('')
  const [merging, setMerging] = useState<LeadView | null>(null)
  const [composerMode, setComposerMode] = useState<string | undefined>(undefined)
  const [composerFocus, setComposerFocus] = useState(0)

  const lead = useQuery({ queryKey: ['lead', recordId], queryFn: ({ signal }) => fetchLead(recordId, signal), retry: false })
  const duplicates = useQuery({
    queryKey: ['lead-duplicates', recordId],
    queryFn: ({ signal }) => fetchLeadDuplicates(recordId, signal),
    retry: false,
  })
  const funnels = useQuery({ queryKey: ['funnels'], queryFn: ({ signal }) => fetchFunnels(signal), enabled: converting })
  const target: ActivityTarget = { type: 'lead', entity: 'sales.lead', resource: 'sales.lead', recordId }
  const { timeline, activities } = useRecordActivity(target)
  // Through the PBX when it is connected (#122); otherwise the phone's own dialler via tel:.
  const dial = useCallAction({ type: 'lead', id: recordId }, lead.data?.phone)
  const modes = useComposerModes(target, { comment: t('record.lead.composer.comment') })

  const mayUpdate = can(user, 'sales.lead', 'update')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['lead', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['lead-duplicates', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'sales.lead', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'sales.lead'] })
    void queryClient.invalidateQueries({ queryKey: ['timeline', 'lead', recordId] })
  }
  const fail = (error: unknown) => setFailure(failureText(error, t('app.error')))
  const succeed = () => {
    setFailure(null)
    refresh()
  }

  const qualify = useMutation({ mutationFn: () => qualifyLead(recordId), onSuccess: succeed, onError: fail })
  const respond = useMutation({ mutationFn: () => recordLeadResponse(recordId), onSuccess: succeed, onError: fail })
  const reject = useMutation({
    mutationFn: () => rejectLead(recordId, reason.trim()),
    onSuccess: () => {
      setRejecting(false)
      setReason('')
      succeed()
    },
    onError: fail,
  })
  const chosenFunnel =
    funnelId || funnels.data?.find((funnel) => funnel.defaultFunnel)?.id || funnels.data?.[0]?.id || ''
  const convert = useMutation({
    mutationFn: () => convertLead(recordId, chosenFunnel),
    onSuccess: (conversion) => {
      void queryClient.invalidateQueries({ queryKey: ['list'] })
      navigate(`${listPath('sales.deal', '/deals')}/${conversion.deal.id}`)
    },
    onError: fail,
  })
  const merge = useMutation({
    mutationFn: (originalId: string) => mergeLead(recordId, originalId),
    onSuccess: () => {
      setMerging(null)
      succeed()
    },
    onError: (error) => {
      setMerging(null)
      fail(error)
    },
  })

  // The SLA timer counts down on screen; a minute is its resolution.
  const now = useNow(60_000)

  if (lead.isLoading) return <RecordSkeleton />
  if (!lead.data) {
    return <RecordFailure error={lead.error} onRetry={() => void lead.refetch()} listPath={listPath('sales.lead', '/leads')} />
  }
  const current = lead.data
  const closed = current.status === 'converted' || current.status === 'rejected' || Boolean(current.duplicateOfId)
  const busy = qualify.isPending || reject.isPending || convert.isPending || respond.isPending || merge.isPending
  const sla = slaOf(current, now)

  // ------------------------------------------------------------------ шапка

  const counterpartyHref = current.convertedCounterpartyId
    ? cardHref(user, 'sales.counterparty', current.convertedCounterpartyId)
    : null
  const meta = [
    <StatusBadge key="status" entity="sales.lead" value={current.status} />,
    sla ? <SlaTimer key="sla" sla={sla} /> : null,
    current.companyName || current.inn ? (
      <span key="company" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" className="muted" />
        {counterpartyHref ? (
          <Link to={counterpartyHref}>{current.companyName ?? current.inn}</Link>
        ) : (
          <span style={{ color: 'var(--fg)' }}>{current.companyName ?? current.inn}</span>
        )}
        {current.inn && <span className="mono muted">{t('record.lead.inn', { inn: current.inn })}</span>}
        {!current.convertedCounterpartyId && <span className="t-xs muted">· {t('record.lead.notCounterparty')}</span>}
      </span>
    ) : null,
    <span key="source" className="row" style={{ gap: 6 }}>
      <Icon name="filter" size="sm" className="muted" />
      {t(`value.sales.lead.source.${current.source}`)}
    </span>,
    current.contactName ? (
      <span key="contact" className="row" style={{ gap: 6 }}>
        <Icon name="user" size="sm" className="muted" />
        <span style={{ color: 'var(--fg)' }}>{current.contactName}</span>
      </span>
    ) : null,
    current.phone ? (
      <a key="phone" href={`tel:${current.phone}`} className="row nowrap" style={{ gap: 4 }}>
        <Icon name="phone" size="sm" />
        {current.phone}
      </a>
    ) : null,
    current.email ? (
      <a key="email" href={`mailto:${current.email}`} className="row nowrap" style={{ gap: 4 }}>
        <Icon name="mail" size="sm" />
        {current.email}
      </a>
    ) : null,
    labelOf(record.values, 'responsible_employee_id'),
  ]

  const open = !closed
  let primary: HeaderAction | null = null
  if (mayUpdate && open && (current.status === 'new' || current.status === 'in_progress')) {
    primary = { id: 'qualify', label: t('record.lead.qualify'), icon: 'arrow-right', onClick: () => qualify.mutate(), disabled: busy }
  } else if (mayUpdate && open && current.status === 'qualified') {
    primary = { id: 'convert', label: t('record.lead.convert'), icon: 'convert', onClick: () => setConverting(true), disabled: busy }
  }

  const secondary: HeaderAction[] = []
  if (mayUpdate && open && !current.firstResponseAt) {
    secondary.push({ id: 'respond', label: t('record.lead.responded'), icon: 'check', onClick: () => respond.mutate(), disabled: busy })
  }
  if (current.phone && dial.action) secondary.push(dial.action)

  const leadDuplicates = (duplicates.data ?? []).filter((candidate) => candidate.id !== recordId)
  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]
  // Converting is open from any open status: qualifying first is a habit, not a rule of the
  // domain, and the button belongs in the menu when it is not the main action.
  if (mayUpdate && open && current.status !== 'qualified') {
    menu.push({ id: 'convert', label: t('record.lead.convertContinue'), icon: 'convert', onSelect: () => setConverting(true) })
  }
  if (mayUpdate && open && leadDuplicates.length > 0) {
    menu.push({ id: 'merge', label: t('record.lead.mergeContinue'), icon: 'convert', onSelect: () => setTab('similar') })
  }
  if (mayUpdate && open) {
    menu.push({ id: 'reject', label: t('record.lead.rejectContinue'), icon: 'close', danger: true, onSelect: () => setRejecting(true) })
  }

  // ------------------------------------------------------------------ полоса и факты

  const order = ['new', 'in_progress', 'qualified', 'converted']
  const at = current.status === 'rejected' ? -1 : order.indexOf(current.status)
  const steps: StageStep[] = order.map((code, index) => ({
    id: code,
    label: t(`value.sales.lead.status.${code}`),
    state: index < at ? 'done' : index === at ? 'current' : 'future',
    meta: code === 'new' && current.createdAt ? t('record.lead.stage.since', { at: shortMoment(current.createdAt, locale) }) : undefined,
  }))

  const facts: Fact[] = [
    {
      id: 'source',
      label: t('record.lead.fact.source'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{t(`value.sales.lead.source.${current.source}`)}</span>,
      sub: [current.utmSource, current.utmCampaign].filter(Boolean).join(' · ') || undefined,
    },
    {
      id: 'received',
      label: t('record.lead.fact.received'),
      value: current.createdAt ? shortMoment(current.createdAt, locale) : t('record.notSet'),
    },
    {
      id: 'sla',
      label: t('record.lead.fact.sla'),
      value: sla ? slaValue(sla, current, locale, t) : t('record.notSet'),
      sub: sla ? slaSub(sla, t) : undefined,
    },
    {
      id: 'responsible',
      label: t('record.lead.fact.responsible'),
      value: (
        <span className="t-md" style={{ fontWeight: 500 }}>
          {labelOf(record.values, 'responsible_employee_id') ?? t('record.lead.unassigned')}
        </span>
      ),
    },
    {
      id: 'territory',
      label: t('record.lead.fact.territory'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{labelOf(record.values, 'territory_id') ?? t('record.notSet')}</span>,
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const similar = (
    <SimilarList
      lead={current}
      leads={leadDuplicates}
      loading={duplicates.isLoading}
      failed={duplicates.isError}
      onMerge={mayUpdate && open ? setMerging : undefined}
    />
  )

  const overview = (
    <div className="layout-2-wide">
      <div className="rec-col">
        {open && (
          <NextStepBlock
            target={target}
            activities={activities}
            missingHint={t('record.lead.next.missingHint')}
            onAssign={
              modes.some((mode) => mode.id === 'task')
                ? () => {
                    setComposerMode('task')
                    setComposerFocus((value) => value + 1)
                  }
                : undefined
            }
          />
        )}
        {modes.length > 0 && (
          <Composer modes={modes} mode={composerMode} onModeChange={setComposerMode} focusKey={composerFocus} />
        )}
        <TaskProposalsPanel entityType="lead" entityId={recordId} />
        <RecordFeed
          timeline={timeline}
          limit={5}
          title={t('record.lead.lastEvents')}
          emptyTitle={t('record.lead.feed.empty')}
          emptyText={t('record.lead.feed.emptyText')}
          footer={
            (timeline.data?.events.length ?? 0) > 5 ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('feed')}>
                {t('record.lead.allFeed')}
              </button>
            ) : undefined
          }
        />
      </div>
      <div className="rec-col">
        <Panel title={t('leadScore.title')}>
          <LeadScorePanel leadId={recordId} />
        </Panel>
        <Panel title={t('record.lead.similar')} count={duplicates.isSuccess ? leadDuplicates.length : null}>
          {similar}
        </Panel>
        {(current.utmSource || current.utmCampaign) && (
          <Panel title={t('record.lead.utm')}>
            <dl className="kv">
              <dt className="mono">utm_source</dt>
              <dd className="mono">{current.utmSource ?? '—'}</dd>
              <dt className="mono">utm_campaign</dt>
              <dd className="mono">{current.utmCampaign ?? '—'}</dd>
            </dl>
          </Panel>
        )}
        <Panel
          title={t('record.lead.details')}
          actions={<EditActions edit={edit} />}
        >
          {form}
        </Panel>
        <Panel title={t('card.tab.customFields')}>
          <CustomFieldsPanel entity={context.entity} recordId={recordId} resource={context.resource} />
        </Panel>
      </div>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.leads'), to: listPath('sales.lead', '/leads') }, { label: current.title }]}
        title={current.title}
        meta={meta}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {dial.outcome}
      <FailureAlert text={failure} />
      {notices}
      {current.status === 'converted' && (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          <span className="fg">{t('record.lead.converted')}</span>
          {current.convertedDealId && (
            <Link to={`${listPath('sales.deal', '/deals')}/${current.convertedDealId}`} className="btn btn-secondary btn-sm">
              {t('lead.actions.openDeal')}
            </Link>
          )}
        </div>
      )}
      {current.status === 'rejected' && (
        <div className="alert alert-neutral" role="status">
          <Icon name="close" />
          <span>{t('lead.actions.rejectedBecause', { reason: current.rejectionReason ?? '—' })}</span>
        </div>
      )}
      {current.duplicateOfId && (
        <div className="alert alert-neutral" role="status">
          <Icon name="convert" />
          <span>{t('record.lead.isDuplicate')}</span>
          <Link to={`${listPath('sales.lead', '/leads')}/${current.duplicateOfId}`} className="btn btn-ghost btn-sm">
            {t('record.lead.openOriginal')}
          </Link>
        </div>
      )}

      <StageBar
        steps={steps}
        ends={[{ id: 'rejected', label: t('value.sales.lead.status.rejected'), tone: 'lost', active: current.status === 'rejected' }]}
        ariaLabel={t('record.lead.stages')}
      />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('record.tab.overview') },
          { id: 'feed', label: t('record.feed.title'), count: timeline.data?.events.length ?? null },
          { id: 'similar', label: t('record.lead.tab.similar'), count: duplicates.isSuccess ? leadDuplicates.length : null },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.lead.sections')}
      >
        {tab === 'overview' && <div style={{ paddingTop: 14 }}>{overview}</div>}
        {tab === 'feed' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <RecordFeed timeline={timeline} emptyTitle={t('record.lead.feed.empty')} emptyText={t('record.lead.feed.emptyText')} />
          </div>
        )}
        {tab === 'similar' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.lead.similar')} count={duplicates.isSuccess ? leadDuplicates.length : null}>
              {similar}
            </Panel>
            <CounterpartyMatches lead={current} />
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      <FormModal
        open={rejecting}
        danger
        icon="close"
        title={t('record.lead.reject.title', { title: current.title })}
        confirmLabel={t('record.lead.reject')}
        canConfirm={reason.trim() !== ''}
        missingHint={t('record.lead.reject.missing')}
        pending={reject.isPending}
        onCancel={() => setRejecting(false)}
        onConfirm={() => reject.mutate()}
      >
        <p className="secondary">{t('record.lead.reject.text')}</p>
        <RequiredText
          label={t('lead.actions.rejectReason')}
          value={reason}
          onChange={setReason}
          placeholder={t('record.lead.reject.placeholder')}
          help={t('record.lead.reject.help')}
        />
      </FormModal>

      <FormModal
        open={converting}
        icon="convert"
        title={t('record.lead.convert.title')}
        confirmLabel={t('record.lead.convert')}
        canConfirm={chosenFunnel !== ''}
        missingHint={t('record.lead.convert.noFunnel')}
        pending={convert.isPending}
        onCancel={() => setConverting(false)}
        onConfirm={() => convert.mutate()}
      >
        <p className="secondary">{t('record.lead.convert.text')}</p>
        <div className="field">
          <label className="label" htmlFor="lead-convert-funnel">
            {t('column.funnel_id')}
          </label>
          <select
            id="lead-convert-funnel"
            className="input select"
            value={chosenFunnel}
            onChange={(event) => setFunnelId(event.target.value)}
          >
            {(funnels.data ?? []).map((funnel) => (
              <option key={funnel.id} value={funnel.id}>
                {funnel.name}
              </option>
            ))}
          </select>
        </div>
      </FormModal>

      <ConfirmModal
        open={merging != null}
        icon="convert"
        title={t('record.lead.merge.title', { title: merging?.title ?? '' })}
        confirmLabel={t('record.lead.merge')}
        pending={merge.isPending}
        onCancel={() => setMerging(null)}
        onConfirm={() => merging && merge.mutate(merging.id)}
      >
        <p>{t('record.lead.merge.text')}</p>
      </ConfirmModal>
    </div>
  )
}

/** Кнопки правки полей в шапке панели «Сведения» — как у сделки. */
export function EditActions({ edit }: { edit: RecordViewProps['edit'] }) {
  const { t } = useApp()
  if (edit.editing) {
    return (
      <>
        <button type="button" className="btn btn-ghost btn-sm" onClick={edit.cancel} disabled={edit.saving}>
          {t('card.cancel')}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={edit.submit} disabled={edit.saving}>
          {edit.saving ? t('card.saving') : t('card.save')}
        </button>
      </>
    )
  }
  return edit.mayUpdate ? (
    <button type="button" className="btn btn-ghost btn-sm" onClick={edit.begin}>
      {t('card.edit')}
    </button>
  ) : null
}

// ------------------------------------------------------------------ похожие

/** Чем похож: сравниваются те же признаки, по которым ищет сервер. */
function matchedOn(lead: LeadView, other: LeadView): string[] {
  const same = (a: string | null | undefined, b: string | null | undefined) =>
    Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase())
  const reasons: string[] = []
  if (same(lead.phone, other.phone)) reasons.push('phone')
  if (same(lead.email, other.email)) reasons.push('email')
  if (same(lead.inn, other.inn)) reasons.push('inn')
  return reasons
}

function SimilarList({
  lead,
  leads,
  loading,
  failed,
  onMerge,
}: {
  lead: LeadView
  leads: LeadView[]
  loading: boolean
  failed: boolean
  onMerge?: (lead: LeadView) => void
}) {
  const { t } = useApp()
  if (loading) return <p className="muted">{t('app.loading')}</p>
  // A failed search is not "no duplicates": silence would read as a confirmed new client.
  if (failed) return <StateView compact kind="error" title={t('record.lead.similar.error')} />
  if (leads.length === 0) {
    return <StateView compact kind="empty" title={t('record.lead.similar.none')} description={t('record.lead.similar.noneText')} />
  }
  return (
    <div className="stack" style={{ gap: 0 }}>
      <div className="list-sub">{t('record.lead.similar.checked')}</div>
      {leads.map((other) => {
        const closedOther = other.status === 'converted' || other.status === 'rejected'
        return (
          <div key={other.id} className="list-item" style={{ alignItems: 'flex-start' }}>
            <Icon name="inbox" className="muted" />
            <div className="stack" style={{ gap: 2, flex: 1 }}>
              <span className="row-wrap" style={{ gap: 6 }}>
                <Link to={`${listPath('sales.lead', '/leads')}/${other.id}`}>{other.title}</Link>
                <StatusBadge entity="sales.lead" value={other.status} />
              </span>
              {matchedOn(lead, other).map((reason) => (
                <span key={reason} className="flag flag-warning">
                  <Icon name="alert" />
                  {t(`record.lead.similar.by.${reason}`)}
                </span>
              ))}
              {other.companyName && <span className="t-sm muted">{other.companyName}</span>}
            </div>
            {/* Only an open lead can be the original: a converted or rejected one is history. */}
            {onMerge && !closedOther && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => onMerge(other)}>
                <Icon name="convert" size="sm" />
                {t('record.lead.merge')}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Контрагенты с тем же ИНН, телефоном или почтой — тем же поиском, что при заведении. */
function CounterpartyMatches({ lead }: { lead: LeadView }) {
  const { t, user } = useApp()
  const criteria = { inn: lead.inn ?? undefined, phone: lead.phone ?? undefined, email: lead.email ?? undefined }
  const allowed = can(user, 'sales.counterparty', 'read')
  const query = useQuery({
    queryKey: ['counterparty-duplicates', criteria],
    queryFn: ({ signal }) => fetchDuplicates(criteria, signal),
    enabled: allowed,
    retry: false,
  })
  if (!allowed) return null
  return (
    <Panel title={t('record.lead.counterparties')} count={query.data?.matches.length ?? null}>
      {query.isLoading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : query.isError ? (
        <StateView compact kind="error" title={t('record.lead.similar.error')} />
      ) : (query.data?.matches ?? []).length === 0 ? (
        <p className="secondary">{t('record.lead.counterparties.none')}</p>
      ) : (
        (query.data?.matches ?? []).map((match) => (
          <div key={match.id} className="list-item" style={{ alignItems: 'flex-start' }}>
            <Icon name="building" className="muted" />
            <div className="stack" style={{ gap: 2, flex: 1 }}>
              <Link to={`${listPath('sales.counterparty', '/counterparties')}/${match.id}`}>{match.name}</Link>
              <span className="t-sm muted mono">{t('record.lead.inn', { inn: match.inn ?? '—' })}</span>
              <span className="row-wrap" style={{ gap: 4 }}>
                {match.matchedOn.map((reason) => (
                  <span key={reason} className="badge">
                    {t(`card.cp.dup.by.${reason}`)}
                  </span>
                ))}
              </span>
            </div>
          </div>
        ))
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------ SLA

type Translate = (key: string, params?: Record<string, string | number>) => string

export interface Sla {
  kind: 'ok' | 'soon' | 'over' | 'met'
  /** Минуты: осталось, просрочено или «ответили за». */
  minutes: number
  /** Доля оставшегося окна, 0–1 (для полосы). */
  left: number
}

/**
 * Состояние часов первого отклика. Нет срока — нет и таймера: выдумывать норматив на
 * клиенте значило бы завести второй SLA, который разойдётся с отчётом руководителя.
 */
export function slaOf(lead: LeadView, now: number): Sla | null {
  const created = lead.createdAt ? Date.parse(lead.createdAt) : NaN
  if (lead.firstResponseAt) {
    const answered = Date.parse(lead.firstResponseAt)
    const minutes = Number.isNaN(created) ? 0 : Math.max(0, Math.round((answered - created) / 60_000))
    return { kind: 'met', minutes, left: 1 }
  }
  if (!lead.firstResponseDueAt) return null
  const due = Date.parse(lead.firstResponseDueAt)
  if (Number.isNaN(due)) return null
  const remaining = due - now
  if (lead.slaBreached || remaining <= 0) {
    return { kind: 'over', minutes: Math.max(0, Math.round(-remaining / 60_000)), left: 0 }
  }
  const window = Number.isNaN(created) ? remaining : Math.max(due - created, 1)
  const left = Math.min(1, remaining / window)
  // A quarter of the window (or a quarter of an hour) is when the manager should drop the rest.
  const soon = left <= 0.25 || remaining <= 15 * 60_000
  return { kind: soon ? 'soon' : 'ok', minutes: Math.round(remaining / 60_000), left }
}

function SlaTimer({ sla }: { sla: Sla }) {
  const { t } = useApp()
  const text = slaText(sla, t)
  return (
    <span className={`sla is-${sla.kind}`} role="timer" aria-label={t('record.lead.sla.aria', { text })}>
      <Icon name={sla.kind === 'met' ? 'check' : sla.kind === 'soon' ? 'alert' : 'clock'} />
      {text}
      {(sla.kind === 'ok' || sla.kind === 'soon') && (
        <span className="bar" aria-hidden="true">
          <i style={{ width: `${Math.round(sla.left * 100)}%` }} />
        </span>
      )}
    </span>
  )
}

function slaText(sla: Sla, t: Translate): string {
  const duration = durationText(sla.minutes, t)
  if (sla.kind === 'met') return t('record.lead.sla.met', { duration })
  if (sla.kind === 'over') return t('record.lead.sla.over', { duration })
  return t('record.lead.sla.left', { duration })
}

function slaValue(sla: Sla, lead: LeadView, locale: string, t: Translate) {
  if (sla.kind === 'met') return <span style={{ color: 'var(--success-text)' }}>{t('record.lead.sla.done')}</span>
  if (sla.kind === 'over') return <span style={{ color: 'var(--danger-text)' }}>{t('record.lead.sla.breached')}</span>
  return (
    <span style={sla.kind === 'soon' ? { color: 'var(--warning-text)' } : undefined}>
      {t('record.lead.sla.until', { at: shortMoment(lead.firstResponseDueAt ?? '', locale) })}
    </span>
  )
}

function slaSub(sla: Sla, t: Translate) {
  return slaText(sla, t)
}

export function durationText(minutes: number, t: Translate): string {
  if (minutes < 60) return t('record.dur.m', { m: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('record.dur.hm', { h: hours, m: minutes % 60 })
  return t('record.dur.dh', { d: Math.floor(hours / 24), h: hours % 24 })
}

/** Момент кратко: время сегодня, дата с временем — раньше. */
function shortMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  const tag = locale === 'en' ? 'en-GB' : 'ru-RU'
  const time = moment.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' })
  const today = new Date()
  if (moment.toDateString() === today.toDateString()) return time
  return `${moment.toLocaleDateString(tag)}, ${time}`
}

/** Текущее время, обновляемое с шагом: таймер на экране обязан идти. */
function useNow(stepMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), stepMs)
    return () => clearInterval(timer)
  }, [stepMs])
  return now
}
